/**
 * Embed (iframe) NodeView for TipTap.
 * Supports drag-to-resize (width via left/right handles, height via bottom handle).
 * Shows a size badge and URL overlay when selected.
 */

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { NodeViewProps } from '@tiptap/react';
import { cn } from '../../../lib/utils';
import { ExternalLink } from 'lucide-react';
import { MediaLayoutFrame } from './MediaLayoutFrame';

const MIN_WIDTH = 200;
const MIN_HEIGHT = 100;
const MAX_WIDTH_RATIO = 1;

type ResizeAxis = 'left' | 'right' | 'bottom' | 'corner';

export const EmbedView: React.FC<NodeViewProps> = (props) => {
    const { node, updateAttributes, deleteNode, selected } = props;
    const containerRef = useRef<HTMLDivElement>(null);
    const [resizing, setResizing] = useState(false);

    const src = (node.attrs as { src?: string }).src ?? '';
    const rawWidth = (node.attrs as { width?: number | string | null }).width;
    const width = typeof rawWidth === 'number' ? rawWidth : null;
    const height = (node.attrs as { height?: number | null }).height ?? 315;
    const title = (node.attrs as { title?: string | null }).title ?? 'Embedded content';

    /* ------------------------------------------------------------------ */
    /* Resize via drag handles                                             */
    /* ------------------------------------------------------------------ */

    const handleMouseDown = useCallback(
        (axis: ResizeAxis, e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setResizing(true);

            const startX = e.clientX;
            const startY = e.clientY;
            const container = containerRef.current;
            if (!container) return;

            const startWidth = width ?? container.offsetWidth;
            const startHeight = height;
            const maxWidth = container.parentElement
                ? container.parentElement.clientWidth * MAX_WIDTH_RATIO
                : 9999;

            const onMouseMove = (ev: MouseEvent) => {
                if (axis === 'left' || axis === 'right') {
                    const delta = axis === 'right' ? ev.clientX - startX : startX - ev.clientX;
                    const nextW = Math.round(Math.max(MIN_WIDTH, Math.min(maxWidth, startWidth + delta)));
                    updateAttributes({ width: nextW });
                } else if (axis === 'bottom') {
                    const delta = ev.clientY - startY;
                    const nextH = Math.round(Math.max(MIN_HEIGHT, startHeight + delta));
                    updateAttributes({ height: nextH });
                } else if (axis === 'corner') {
                    const dX = ev.clientX - startX;
                    const dY = ev.clientY - startY;
                    const nextW = Math.round(Math.max(MIN_WIDTH, Math.min(maxWidth, startWidth + dX)));
                    const nextH = Math.round(Math.max(MIN_HEIGHT, startHeight + dY));
                    updateAttributes({ width: nextW, height: nextH });
                }
            };

            const onMouseUp = () => {
                setResizing(false);
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
            };

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        },
        [width, height, updateAttributes]
    );

    useEffect(() => {
        if (resizing) {
            document.body.style.userSelect = 'none';
        }
        return () => {
            document.body.style.userSelect = '';
        };
    }, [resizing]);

    if (!src) return null;

    const showHandles = selected || resizing;
    const displayWidth = width ? `${width}px` : '100%';

    return (
        <MediaLayoutFrame
            node={node}
            updateAttributes={updateAttributes}
            deleteNode={deleteNode}
            selected={selected}
            className={cn(
                'tiptap-embed-wrapper',
                selected && 'tiptap-embed--selected',
                resizing && 'tiptap-embed--resizing'
            )}
        >
            <div
                ref={containerRef}
                className="tiptap-embed relative"
                style={{
                    width: displayWidth,
                    maxWidth: '100%',
                }}
            >
                <iframe
                    src={src}
                    width="100%"
                    height={height}
                    title={title ?? 'Embedded content'}
                    frameBorder="0"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-presentation allow-forms"
                    referrerPolicy="no-referrer-when-downgrade"
                    loading="lazy"
                    className="tiptap-embed-iframe"
                    style={{ pointerEvents: resizing ? 'none' : undefined }}
                />

                {/* Resize handles */}
                {showHandles && (
                    <>
                        {/* Left / right — width */}
                        <div
                            className="tiptap-resize-handle tiptap-resize-handle--left"
                            onMouseDown={(e) => handleMouseDown('left', e)}
                        />
                        <div
                            className="tiptap-resize-handle tiptap-resize-handle--right"
                            onMouseDown={(e) => handleMouseDown('right', e)}
                        />
                        {/* Bottom — height */}
                        <div
                            className="tiptap-embed-resize-handle-bottom"
                            onMouseDown={(e) => handleMouseDown('bottom', e)}
                        />
                        {/* Corner — both */}
                        <div
                            className="tiptap-embed-resize-handle-corner"
                            onMouseDown={(e) => handleMouseDown('corner', e)}
                        />
                    </>
                )}

                {/* Size badge while resizing */}
                {resizing && (
                    <span className="tiptap-resize-badge">
                        {width ?? '100%'} × {height}px
                    </span>
                )}

                {/* URL badge when selected (not resizing) */}
                {selected && !resizing && (
                    <div className="tiptap-embed-url-badge">
                        <ExternalLink className="h-3 w-3 shrink-0" />
                        <span className="truncate">{src}</span>
                    </div>
                )}
            </div>
        </MediaLayoutFrame>
    );
};

EmbedView.displayName = 'EmbedView';
